import { useCallback, useEffect, useRef } from 'react';
import {
  IntegrityEventType,
  IntegrityEventInput,
  StartIntegrityMonitoringSessionInput,
  useReportIntegrityEventsMutation,
  useEndIntegrityMonitoringSessionMutation,
} from 'generated/graphql';

const FLUSH_INTERVAL_MS = 10_000;
const MAX_EVENTS_PER_BATCH = 100; // mirrors the backend per-call cap
const START_RETRY_BASE_DELAY_MS = 5_000;
const START_RETRY_MAX_DELAY_MS = 60_000;
// A resize gesture (window drag, devtools panel drag) fires a stream of
// resize events; the gesture only counts as finished after this much quiet.
const RESIZE_SETTLE_MS = 2_000;
const RESIZE_THRESHOLD_PX = 100;
// Sub-second blur/focus blips (clicking devtools, browser chrome, another
// monitor) are not real tab switches — don't report them.
const MIN_TAB_AWAY_SECONDS = 1;
const DEVTOOLS_GAP_THRESHOLD_PX = 160;
const DEVTOOLS_POLL_INTERVAL_MS = 2_000;

// Owner-specific session starter: calls the owning test type's start mutation
// (AI interview, cognitive test, personality test) and returns the created or
// reopened session. The backend start mutations are idempotent, so retrying
// is always safe. Throw on failure to trigger the hook's retry backoff.
export type StartIntegritySession = (
  input: StartIntegrityMonitoringSessionInput,
) => Promise<{ id: string; startedAt: string } | null>;

type HookProps = {
  enabled: boolean;
  startSession: StartIntegritySession;
};

// Durational events (tab away, camera off) are held open until they close so
// a single row carries the full range; anything still open is closed with the
// current offset when the session ends.
type OpenEvent = {
  eventType: IntegrityEventType;
  startedAtSeconds: number;
  metadata?: Record<string, unknown> | null;
};

export type ReportIntegrityEvent = (
  eventType: IntegrityEventType,
  metadata?: Record<string, unknown> | null,
  range?: { startedAtSeconds: number; endedAtSeconds?: number | null },
) => void;

// Returns whether the event is open after the call — false while the
// monitoring session doesn't exist yet, so callers can retry instead of
// assuming the open registered.
// `startedSecondsAgo` backdates the start for detectors that debounce before
// opening, so the stored range covers the real occurrence.
export type OpenIntegrityEvent = (
  eventType: IntegrityEventType,
  metadata?: Record<string, unknown>,
  options?: { startedSecondsAgo?: number },
) => boolean;

export type CloseIntegrityEvent = (
  eventType: IntegrityEventType,
  options?: { minDurationSeconds?: number; endedSecondsAgo?: number },
) => void;

export type IntegrityMonitoringHandle = {
  reportEvent: ReportIntegrityEvent;
  // Open/close a durational event so external detectors (e.g. camera vision)
  // can hold ranges the same way the built-in detectors do.
  openEvent: OpenIntegrityEvent;
  closeEvent: CloseIntegrityEvent;
};

// Owner-agnostic integrity monitoring core: starts a session (via the
// injected owner-specific mutation) while `enabled`, listens for in-browser
// integrity signals, batches them and flushes every 10s, and ends the session
// when disabled/unmounted. Owner wrappers add their source-specific detectors
// (e.g. LiveKit camera-off for AI interviews) through the returned handle.
export function useIntegrityMonitoring({
  enabled,
  startSession,
}: HookProps): IntegrityMonitoringHandle {
  const sessionIdRef = useRef<string | null>(null);
  // Offsets are anchored to the server-side session startedAt so they stay
  // consistent across rejoins (the backend reopens the same session).
  const sessionStartMsRef = useRef<number | null>(null);
  const bufferRef = useRef<IntegrityEventInput[]>([]);
  const openEventsRef = useRef<Map<IntegrityEventType, OpenEvent>>(new Map());
  const lastCopiedTextRef = useRef<string | null>(null);
  const lastWindowSizeRef = useRef<{ width: number; height: number }>({
    width: window.innerWidth,
    height: window.innerHeight,
  });
  const devtoolsOpenRef = useRef(false);

  const [reportEvents] = useReportIntegrityEventsMutation();
  const [endSession] = useEndIntegrityMonitoringSessionMutation();

  const nowOffsetSeconds = useCallback((): number => {
    if (!sessionStartMsRef.current) return 0;
    // Rounded to one decimal.
    return Math.max(
      0,
      Math.round((Date.now() - sessionStartMsRef.current) / 100) / 10,
    );
  }, []);

  const reportEvent = useCallback<ReportIntegrityEvent>(
    (eventType, metadata, range) => {
      if (!sessionStartMsRef.current) return;
      bufferRef.current.push({
        eventType,
        startedAt: range?.startedAtSeconds ?? nowOffsetSeconds(),
        endedAt: range?.endedAtSeconds ?? null,
        metadata: metadata ?? null,
      });
    },
    [nowOffsetSeconds],
  );

  const openEvent = useCallback<OpenIntegrityEvent>(
    (eventType, metadata, options) => {
      if (!sessionStartMsRef.current) return false;
      if (openEventsRef.current.has(eventType)) return true;
      openEventsRef.current.set(eventType, {
        eventType,
        startedAtSeconds: Math.max(
          0,
          Math.round(
            (nowOffsetSeconds() - (options?.startedSecondsAgo ?? 0)) * 10,
          ) / 10,
        ),
        metadata,
      });
      return true;
    },
    [nowOffsetSeconds],
  );

  const closeEvent = useCallback(
    (
      eventType: IntegrityEventType,
      options?: { minDurationSeconds?: number; endedSecondsAgo?: number },
    ) => {
      const open = openEventsRef.current.get(eventType);
      if (!open) return;
      openEventsRef.current.delete(eventType);
      const endedAtSeconds = Math.max(
        open.startedAtSeconds,
        Math.round(
          (nowOffsetSeconds() - (options?.endedSecondsAgo ?? 0)) * 10,
        ) / 10,
      );
      if (
        options?.minDurationSeconds != null &&
        endedAtSeconds - open.startedAtSeconds < options.minDurationSeconds
      ) {
        return;
      }
      reportEvent(open.eventType, open.metadata, {
        startedAtSeconds: open.startedAtSeconds,
        endedAtSeconds,
      });
    },
    [nowOffsetSeconds, reportEvent],
  );

  const closeAllOpenEvents = useCallback(() => {
    for (const eventType of [...openEventsRef.current.keys()]) {
      closeEvent(eventType);
    }
  }, [closeEvent]);

  const flushEvents = useCallback(() => {
    const sessionId = sessionIdRef.current;
    if (!sessionId || bufferRef.current.length === 0) return;

    const batch = bufferRef.current.splice(0, MAX_EVENTS_PER_BATCH);
    reportEvents({ variables: { sessionId, events: batch } }).catch(() => {
      bufferRef.current.unshift(...batch);
    });
  }, [reportEvents]);

  // Start (or reopen, on rejoin) the session when monitoring becomes active.
  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;

    async function start(attempt: number) {
      const input = await getDeviceInfo();
      if (cancelled) return;
      try {
        const session = await startSession(input);
        if (cancelled || !session) return;
        sessionIdRef.current = session.id;
        sessionStartMsRef.current = new Date(session.startedAt).getTime();
      } catch {
        // Monitoring must never break the assessment, but a transient failure
        // (network blip at start) must not kill it either: without a session
        // every event would be silently dropped. Keep retrying with capped
        // backoff while monitoring stays active.
        retryTimer = setTimeout(
          () => start(attempt + 1),
          Math.min(
            START_RETRY_MAX_DELAY_MS,
            START_RETRY_BASE_DELAY_MS * 2 ** attempt,
          ),
        );
      }
    }

    start(0);

    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [enabled, startSession]);

  // Tab/window switches — held open while away so one event covers the range.
  useEffect(() => {
    if (!enabled) return;

    const handleAway = () => openEvent(IntegrityEventType.TabSwitch);
    const handleBack = () =>
      closeEvent(IntegrityEventType.TabSwitch, {
        minDurationSeconds: MIN_TAB_AWAY_SECONDS,
      });

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') handleAway();
      else handleBack();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleAway);
    window.addEventListener('focus', handleBack);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleAway);
      window.removeEventListener('focus', handleBack);
    };
  }, [enabled, openEvent, closeEvent]);

  // Paste from outside the assessment (clipboard content that was not copied
  // within this page).
  useEffect(() => {
    if (!enabled) return;

    const handleCopy = () => {
      lastCopiedTextRef.current = document.getSelection()?.toString() ?? null;
    };

    const handlePaste = (e: ClipboardEvent) => {
      const pastedText = e.clipboardData?.getData('text') ?? '';
      if (!pastedText || pastedText === lastCopiedTextRef.current) return;
      reportEvent(IntegrityEventType.PastedFromExternalSources, {
        pastedTextLength: pastedText.length,
      });
    };

    document.addEventListener('copy', handleCopy);
    document.addEventListener('paste', handlePaste);
    return () => {
      document.removeEventListener('copy', handleCopy);
      document.removeEventListener('paste', handlePaste);
    };
  }, [enabled, reportEvent]);

  // Window resize — one event per gesture, not per fired resize. The stream
  // of resize events during a drag keeps extending the gesture; it's reported
  // once the window has been still for RESIZE_SETTLE_MS, and only if the net
  // size change against the last reported size clears the threshold.
  useEffect(() => {
    if (!enabled) return;

    let resizeTimer: ReturnType<typeof setTimeout> | null = null;
    let gesture: { startedAtSeconds: number; lastAtSeconds: number } | null =
      null;

    const handleResize = () => {
      const offset = nowOffsetSeconds();
      if (!gesture) {
        gesture = { startedAtSeconds: offset, lastAtSeconds: offset };
      } else {
        gesture.lastAtSeconds = offset;
      }

      if (resizeTimer) clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        const finished = gesture;
        gesture = null;
        if (!finished) return;

        const { width: prevW, height: prevH } = lastWindowSizeRef.current;
        const newW = window.innerWidth;
        const newH = window.innerHeight;

        // Baseline only moves when a gesture is reported, so repeated
        // sub-threshold nudges still add up to a reportable change.
        if (
          Math.abs(newW - prevW) > RESIZE_THRESHOLD_PX ||
          Math.abs(newH - prevH) > RESIZE_THRESHOLD_PX
        ) {
          lastWindowSizeRef.current = { width: newW, height: newH };
          reportEvent(
            IntegrityEventType.WindowResize,
            {
              fromWidth: prevW,
              fromHeight: prevH,
              innerWidth: newW,
              innerHeight: newH,
            },
            {
              startedAtSeconds: finished.startedAtSeconds,
              endedAtSeconds:
                finished.lastAtSeconds > finished.startedAtSeconds
                  ? finished.lastAtSeconds
                  : null,
            },
          );
        }
      }, RESIZE_SETTLE_MS);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      if (resizeTimer) clearTimeout(resizeTimer);
    };
  }, [enabled, nowOffsetSeconds, reportEvent]);

  // Developer console — heuristic: docked devtools shrink the inner viewport
  // while the outer window stays put, so the outer/inner gap suddenly grows.
  // The gap is compared against a baseline (the smallest gap seen at the
  // current zoom level), never against an absolute size: outer* is measured
  // in device-independent pixels while inner* is in CSS pixels, so browser
  // chrome plus any page zoom give the gap a large standing value that an
  // absolute threshold misreads as devtools on the very first poll. Zoom
  // changes rescale the inner dimensions, so the baseline resets whenever
  // devicePixelRatio changes instead of comparing across zoom levels.
  useEffect(() => {
    if (!enabled) return;

    let baseline: { dpr: number; gapX: number; gapY: number } | null = null;

    const poll = setInterval(() => {
      const gapX = window.outerWidth - window.innerWidth;
      const gapY = window.outerHeight - window.innerHeight;
      const dpr = window.devicePixelRatio;

      if (!baseline || baseline.dpr !== dpr) {
        baseline = { dpr, gapX, gapY };
        devtoolsOpenRef.current = false;
        return;
      }
      baseline.gapX = Math.min(baseline.gapX, gapX);
      baseline.gapY = Math.min(baseline.gapY, gapY);

      const open =
        gapX - baseline.gapX > DEVTOOLS_GAP_THRESHOLD_PX ||
        gapY - baseline.gapY > DEVTOOLS_GAP_THRESHOLD_PX;

      if (open && !devtoolsOpenRef.current) {
        devtoolsOpenRef.current = true;
        reportEvent(IntegrityEventType.DeveloperConsoleOpened, {
          outerInnerGapX: gapX,
          outerInnerGapY: gapY,
        });
      } else if (!open) {
        devtoolsOpenRef.current = false;
      }
    }, DEVTOOLS_POLL_INTERVAL_MS);

    return () => clearInterval(poll);
  }, [enabled, reportEvent]);

  // Periodic flush; on leave close open ranges, flush, and end the session.
  useEffect(() => {
    if (!enabled) return;

    const interval = setInterval(flushEvents, FLUSH_INTERVAL_MS);

    return () => {
      clearInterval(interval);
      closeAllOpenEvents();
      flushEvents();

      const sessionId = sessionIdRef.current;
      if (sessionId) {
        endSession({ variables: { sessionId } }).catch(() => {
          // Best effort: an unreachable network at leave keeps the session
          // open; the backend tolerates never-ended sessions.
        });
      }
    };
  }, [enabled, flushEvents, closeAllOpenEvents, endSession]);

  return { reportEvent, openEvent, closeEvent };
}

async function getDeviceInfo(): Promise<{
  deviceOs: string | null;
  deviceType: string;
  browserVersion: string | null;
  screenIsExtended: boolean | null;
  timezone: string;
  metadata: Record<string, unknown>;
}> {
  const metadata: Record<string, unknown> = {
    userAgent: navigator.userAgent,
    deviceType: navigator.maxTouchPoints > 0 ? 'touch' : 'desktop',
    screenWidth: window.screen.width,
    screenHeight: window.screen.height,
    maxTouchPoints: navigator.maxTouchPoints,
  };

  let deviceOs: string | null = null;
  let deviceType: string | null = null;
  let browserVersion: string | null = null;

  // Chromium exposes structured, high-entropy values; elsewhere fall back to
  // parsing the user-agent string.
  try {
    // @ts-expect-error -- userAgentData is not in all TS lib typings yet
    if (typeof navigator.userAgentData?.mobile === 'boolean') {
      // @ts-expect-error -- userAgentData is not in all TS lib typings yet
      deviceType = navigator.userAgentData.mobile ? 'Mobile' : 'Desktop';
    }
    // @ts-expect-error -- userAgentData is not in all TS lib typings yet
    const uaData = await navigator.userAgentData?.getHighEntropyValues([
      'platform',
      'platformVersion',
      'fullVersionList',
    ]);
    if (uaData) {
      deviceOs = [uaData.platform, uaData.platformVersion]
        .filter(Boolean)
        .join(' ');
      const brand = (
        uaData.fullVersionList as
          | Array<{ brand: string; version: string }>
          | undefined
      )?.find((b) => !/Not.*Brand/i.test(b.brand));
      if (brand) browserVersion = `${brand.brand} ${brand.version}`;
    }
  } catch {
    // Not supported — fall back below.
  }

  if (!deviceOs) {
    deviceOs = parseOsFromUserAgent(navigator.userAgent);
  }
  if (!browserVersion) {
    browserVersion = parseBrowserFromUserAgent(navigator.userAgent);
  }

  // Chromium-only API: null (unknown) when unsupported, so a single screen
  // is never conflated with "couldn't detect".
  const screenIsExtended =
    'isExtended' in window.screen
      ? !!(window.screen as Screen & { isExtended?: boolean }).isExtended
      : null;

  return {
    deviceOs,
    deviceType:
      deviceType ??
      (/Mobi|Android|iPhone|iPad/i.test(navigator.userAgent)
        ? 'Mobile'
        : 'Desktop'),
    browserVersion,
    screenIsExtended,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    metadata,
  };
}

function parseOsFromUserAgent(userAgent: string): string | null {
  const patterns: Array<[RegExp, (m: RegExpMatchArray) => string]> = [
    [/Windows NT ([\d.]+)/, (m) => `Windows NT ${m[1]}`],
    [/iPhone OS ([\d_]+)/, (m) => `iOS ${m[1].replace(/_/g, '.')}`],
    [/iPad; CPU OS ([\d_]+)/, (m) => `iPadOS ${m[1].replace(/_/g, '.')}`],
    [/Mac OS X ([\d_.]+)/, (m) => `macOS ${m[1].replace(/_/g, '.')}`],
    [/Android ([\d.]+)/, (m) => `Android ${m[1]}`],
    [/CrOS [\w]+ ([\d.]+)/, (m) => `ChromeOS ${m[1]}`],
    [/Linux/, () => 'Linux'],
  ];
  for (const [pattern, format] of patterns) {
    const match = userAgent.match(pattern);
    if (match) return format(match);
  }
  return null;
}

function parseBrowserFromUserAgent(userAgent: string): string | null {
  const match =
    userAgent.match(/(Firefox|Edg|OPR|Chrome|Safari)\/([\d.]+)/) ?? null;
  if (!match) return null;
  const names: Record<string, string> = {
    Edg: 'Edge',
    OPR: 'Opera',
  };
  return `${names[match[1]] ?? match[1]} ${match[2]}`;
}
